/**
 * Per-class bot kits: which button does what for the awareness layer, plus the APL.
 * Ids come from @keystone/content (PLAN §4). Unknown classes get a generic kit derived
 * from their binds and ability tags, so bots still act on placeholder content.
 */
import type { AbilityDef, AbilityId, ClassDef, Command, ContentRegistry, ResourceId, Role } from '@keystone/sim';
import type { BotMemory } from './memory'; // +ranger-specs
import type { Perception } from './perception'; // +ranger-specs
import { ABILITY, AURA, BASE_ABILITY, BASE_AURA, BASE_CLASS, CLASS } from '@keystone/content';
import type { AplEntry } from './apl';
import { SPEC_KITS } from './specKits/index';

export interface HealerSpells {
  /** F13: AoE spender (Dawnsong): used when several allies are hurt and the spender is affordable. */
  aoeSpender?: AbilityId;
  /** F13: burst cooldown (Dawnbringer); pressed again while its follow-up aura is up (Sunfall, F2). */
  burst?: AbilityId;
  /** F13: aura that marks the burst follow-up as offered (Sunfall Ready). */
  burstFollowupAura?: string;
  /** Stacks that power the follow-up (Dawnlight). */
  burstFollowupStacks?: string;
  /** Proc that makes spenders free (Divine Purpose). */
  freeSpenderAura?: string;
  /** Proc that makes Flash Heal instant (Infusion of Light). */
  infusionAura?: string;
  /** Cast-time efficient single-target heal. */
  flash?: AbilityId;
  /** Instant heal on cooldown (can also be used on enemies if the content allows it). */
  shock?: AbilityId;
  /** Spender: big instant heal. */
  spender?: AbilityId;
  spenderResource?: ResourceId;
  spenderCost?: number;
  manaResource?: ResourceId;
  /**
   * +acolyte-specs: in-combat triage threshold for the flash heal (missing + predicted, fraction of
   * max HP; default 0.2). A Lightbinder heals through Absolution and keeps its cast heal for emergencies.
   */
  flashAt?: number;
  /** +acolyte-specs: never cast the flash heal on myself (a Hemomancer refills itself by fighting). */
  flashNotSelf?: boolean;
  /**
   * +acolyte-specs: below `hp` of my own health, flash only allies under `allyBelow` (a health-cost
   * healer refills before it pours more blood out, unless someone is about to die).
   */
  flashSelfFloor?: { hp: number; allyBelow: number };
}

export interface ClassKit {
  classId: string;
  role: Role;
  /** Ranged units keep distance; melee stand at the target. */
  ranged: boolean;
  interrupt?: AbilityId;
  /** F13: second interrupt used when `interrupt` is on cooldown (Warden Aegis Throw). */
  secondaryInterrupt?: AbilityId;
  dispel?: AbilityId;
  taunt?: AbilityId;
  /** Tank: pull a ranged caster into melee. */
  grip?: AbilityId;
  /** Tank: short off-GCD mitigation (buster answer). */
  activeMitigation?: AbilityId;
  activeMitigationAura?: string;
  /** Tank: big cooldown (low HP / big buster). */
  majorDefensive?: AbilityId;
  majorDefensiveAura?: string;
  /** DPS/healer personal defensive. */
  personalDefensive?: AbilityId;
  externalDefensive?: AbilityId;
  externalDefensiveAura?: string;
  groupHeal?: AbilityId;
  heals?: HealerSpells;
  /**
   * +basekits: out-of-combat presses (party buffs, stealth before a pull, run speed), tried
   * whenever nothing is engaged, before the rotation.
   */
  oocBuffs?: AplEntry[];
  /** +basekits: a forward blink used to break away from a rooted melee target (Fold Step). */
  blink?: AbilityId;
  /** Rotation (APL) used when awareness has nothing to do. */
  apl: AplEntry[];
  /**
   * +ranger-specs: the spec's own heal planner (a heal-over-time healer rolls its HoTs), run after
   * dispels and the external defensive, before the generic triage (null = fall through).
   */
  healPlan?: (p: Perception, mem: BotMemory) => Command[] | null;
  /** +ranger-specs: off-GCD reflexes checked every think, even while the GCD runs (the Deadeye's Snap Shut). */
  reflex?: (p: Perception, mem: BotMemory) => Command[] | null;
}

const W = ABILITY.warden;
const L = ABILITY.lightbinder;
const B = ABILITY.blademaster;
const S = ABILITY.stormcaller;
const WA = BASE_ABILITY.warrior;
const AC = BASE_ABILITY.acolyte;
const RO = BASE_ABILITY.rogue;
const MA = BASE_ABILITY.mage;
const RA = BASE_ABILITY.ranger;

/** Zone def id used by Consecrated Ground (content convention: same as the ability id). */
export const CONSECRATED_ZONE = W.consecratedGround;

/** Stormcaller talent-only transform auras (ids from progression's talent data; bots can't import it). */
const SC_TALENT_AURA = { thundercrashReady: 'stormcaller.thundercrash_ready', lavaBeamReady: 'stormcaller.lava_beam_ready' } as const;

export const KITS: Readonly<Record<string, ClassKit>> = {
  [CLASS.warden]: {
    classId: CLASS.warden,
    role: 'tank',
    ranged: false,
    interrupt: W.shieldBash,
    secondaryInterrupt: W.aegisThrow,
    taunt: W.taunt,
    grip: W.grip,
    activeMitigation: W.shieldWallBash,
    activeMitigationAura: AURA.shieldWallBash,
    majorDefensive: W.lastStand,
    majorDefensiveAura: AURA.lastStand,
    // Rotation rework (docs/design/classes/warden.md §6): press what glows, keep Slam rolling, catch
    // the Aegis, fill with Cleave. The bot always casts base ids; the sim resolves key 5 to Aegis
    // Recall while it is up. `finesse` lines are the skill-dial extras (novices skip them).
    apl: [
      // Catch the shield (off-GCD) as soon as the GCD frees up after a throw.
      { ability: W.aegisThrow, when: [{ type: 'selfAura', auraId: AURA.aegisRecallReady }] },
      // Big heal: emergency, right after Last Stand (Q → 3), capped Resolve.
      {
        ability: W.bulwarkStrike,
        when: [{ type: 'any', of: [{ type: 'selfHpBelow', pct: 0.5 }, { type: 'selfAura', auraId: AURA.lastStand }, { type: 'resource', resource: 'resolve', atLeast: 90 }] }],
      },
      // Cash Bastion at 4+ before the next builder overcaps it (Slam +2, Opening Riposte +2, Aegis +3).
      // Resolve, not Bastion, limits how many Bulwarks fit in a minute, so keeping the bar low is
      // what stops the overcap. Novices wait for the glow at 6.
      { ability: W.bulwarkStrike, finesse: true, when: [{ type: 'selfAura', auraId: AURA.bastion, minStacks: 4 }] },
      { ability: W.bulwarkStrike, when: [{ type: 'selfAura', auraId: AURA.bastion, minStacks: 6 }] },
      // Consecrated Ground on cooldown (resets Aegis, grants Opening): AoE first, then everywhere.
      { ability: W.consecratedGround, target: 'groundSelf', when: [{ type: 'inCombat' }, { type: 'not', cond: { type: 'inOwnZone', zoneId: CONSECRATED_ZONE } }] },
      // AoE: Aegis Throw and the Riposte cone move above Slam (Resolve income triples in AoE).
      // Aegis Throw silences its target: disciplined tanks don't spend it on a filler bolt while the
      // pull holds a cast that matters (it is the backup interrupt, kick.ts).
      { ability: W.aegisThrow, when: [{ type: 'targets', atLeast: 3 }, { type: 'not', cond: { type: 'targetFillerCast' } }] },
      { ability: W.riposte, when: [{ type: 'targets', atLeast: 3 }, { type: 'any', of: [{ type: 'selfAura', auraId: AURA.opening }, { type: 'resource', resource: 'resolve', atLeast: 45 }] }] },
      { ability: W.shieldSlam },
      // Spend Opening before it caps or expires (or on any cleave target). Finesse: not while Resolve
      // is short of the next Bulwark at 4+ Bastion (Resolve is the binding resource; the cash comes first).
      {
        ability: W.riposte,
        when: [
          { type: 'any', of: [{ type: 'not', cond: { type: 'selfAura', auraId: AURA.bastion, minStacks: 4 } }, { type: 'resource', resource: 'resolve', atLeast: 65 }] },
          {
            type: 'any',
            of: [
              { type: 'selfAura', auraId: AURA.opening, minStacks: 2 },
              { type: 'selfAuraRemainingBelow', auraId: AURA.opening, ms: 3000 },
              { type: 'all', of: [{ type: 'selfAura', auraId: AURA.opening }, { type: 'targets', atLeast: 2 }] },
            ],
          },
        ],
      },
      { ability: W.aegisThrow, when: [{ type: 'not', cond: { type: 'targetFillerCast' } }] },
      // A lone Opening waits while Resolve is needed for the next Bulwark (Bastion 4+).
      { ability: W.riposte, when: [{ type: 'selfAura', auraId: AURA.opening }, { type: 'any', of: [{ type: 'not', cond: { type: 'selfAura', auraId: AURA.bastion, minStacks: 4 } }, { type: 'resource', resource: 'resolve', atLeast: 65 }] }] },
      // AoE Resolve dump.
      { ability: W.riposte, when: [{ type: 'targets', atLeast: 2 }, { type: 'resource', resource: 'resolve', atLeast: 65 }] },
      { ability: W.cleaveStrike },
    ],
  },
  [CLASS.lightbinder]: {
    classId: CLASS.lightbinder,
    role: 'healer',
    ranged: true,
    dispel: L.cleanse,
    externalDefensive: L.guardiansAegis,
    externalDefensiveAura: AURA.guardiansAegis,
    groupHeal: L.radiantWave,
    personalDefensive: L.divineShield,
    heals: {
      flash: L.flashHeal,
      shock: L.holyShock,
      spender: L.wordOfLight,
      spenderResource: 'holy_power',
      spenderCost: 3,
      manaResource: 'mana',
      aoeSpender: L.dawnsong,
      burst: L.dawnbringer,
      burstFollowupAura: AURA.sunfallReady,
      burstFollowupStacks: AURA.dawnlight,
      freeSpenderAura: AURA.divinePurpose,
      infusionAura: AURA.infusionOfLight,
    },
    // Rotation when triage has nothing to do (docs/design/classes/lightbinder.md §6). Key 6 is pressed
    // as L.dawnbringer: while Sunfall is offered the sim resolves the press to Sunfall (F2).
    apl: [
      // Sunfall at 5 Dawnlight, or before the window closes, or when the group is hurt.
      { ability: L.dawnbringer, target: 'enemy', when: [{ type: 'selfAura', auraId: AURA.sunfallReady }, { type: 'selfAura', auraId: AURA.dawnlight, minStacks: 5 }] },
      { ability: L.dawnbringer, target: 'enemy', when: [{ type: 'selfAura', auraId: AURA.sunfallReady }, { type: 'selfAura', auraId: AURA.dawnlight }, { type: 'selfAuraRemainingBelow', auraId: AURA.sunfallReady, ms: 3500 }] },
      { ability: L.dawnbringer, target: 'enemy', when: [{ type: 'selfAura', auraId: AURA.sunfallReady }, { type: 'selfAura', auraId: AURA.dawnlight }, { type: 'injuredAllies', below: 0.7, atLeast: 3 }] },
      // No enemy left (the pull ended inside the window): Sunfall still heals, cast it on myself.
      { ability: L.dawnbringer, target: 'self', when: [{ type: 'selfAura', auraId: AURA.sunfallReady }, { type: 'selfAura', auraId: AURA.dawnlight }, { type: 'not', cond: { type: 'inCombat' } }] },
      // Free spender (Divine Purpose) and the Holy Power cap: Dawnsong tops the group off and builds Dawnlight.
      { ability: L.dawnsong, when: [{ type: 'inCombat' }, { type: 'selfAura', auraId: AURA.divinePurpose }] },
      { ability: L.dawnsong, when: [{ type: 'inCombat' }, { type: 'resource', resource: 'holy_power', atLeast: 5 }] },
      // One generator away from the cap: spend now (Glorious Dawn or a Greater Judgment would overflow).
      { ability: L.dawnsong, finesse: true, when: [{ type: 'inCombat' }, { type: 'resource', resource: 'holy_power', atLeast: 4 }] },
      // Major window on cooldown in combat.
      // (Not into a dying target: the Sunfall window would run out of combat with nothing to build on.)
      { ability: L.dawnbringer, target: 'enemy', when: [{ type: 'inCombat' }, { type: 'selfLacksAura', auraId: AURA.sunfallReady }, { type: 'targetHpAbove', pct: 0.25 }] },
      // AoE pulls spread the damage: spend Holy Power on Dawnsong as soon as it is affordable.
      { ability: L.dawnsong, when: [{ type: 'inCombat' }, { type: 'targets', atLeast: 3, radius: 12 }, { type: 'resource', resource: 'holy_power', atLeast: 3 }, { type: 'injuredAllies', below: 0.95, atLeast: 2 }] },
      // Inside Avenging Light, build Dawnlight: spender, Radiant Wave, Greater Judgment, Holy Shock.
      { ability: L.dawnsong, when: [{ type: 'selfAura', auraId: AURA.avengingLight }, { type: 'resource', resource: 'holy_power', atLeast: 3 }] },
      { ability: L.radiantWave, when: [{ type: 'inCombat' }, { type: 'selfAura', auraId: AURA.avengingLight }] },
      { ability: L.judgment, when: [{ type: 'selfAura', auraId: AURA.avengingLight }, { type: 'selfAura', auraId: AURA.infusionOfLight }] },
      // Radiant Wave on cooldown in combat (it also grants Infusion and Holy Power).
      { ability: L.radiantWave, finesse: true, when: [{ type: 'inCombat' }] },
      // Holy Shock before it caps, with Glorious Dawn, or in Avenging Light (guaranteed Infusion).
      {
        ability: L.holyShock,
        target: 'enemy',
        when: [
          { type: 'inCombat' },
          { type: 'any', of: [{ type: 'charges', ability: L.holyShock, atLeast: 2 }, { type: 'selfAura', auraId: AURA.gloriousDawn }, { type: 'selfAura', auraId: AURA.avengingLight }] },
        ],
      },
      // Greater Judgment: spend Infusion before it caps or expires (the filler Judgment spends the rest).
      { ability: L.judgment, when: [{ type: 'inCombat' }, { type: 'selfAura', auraId: AURA.infusionOfLight, minStacks: 2 }] },
      { ability: L.judgment, finesse: true, when: [{ type: 'inCombat' }, { type: 'selfAuraRemainingBelow', auraId: AURA.infusionOfLight, ms: 4000 }] },
      { ability: L.holyShock, target: 'enemy', when: [{ type: 'inCombat' }] },
      { ability: L.judgment, when: [{ type: 'inCombat' }] },
    ],
  },
  [CLASS.blademaster]: {
    classId: CLASS.blademaster,
    role: 'dps',
    ranged: false,
    interrupt: B.pummel,
    personalDefensive: B.dieByTheSword,
    // Rotation rework (docs/design/classes/blademaster.md §6): spend procs before they cap, keep
    // Rend / Colossus Smash rolling, cash Wounds with Mortal Strike, pool Rage for it before Slam.
    // The bot keeps pressing base ids: Bladestorm resolves to Ravager's Finale and Slam to the
    // Shockwave talent form in the sim (F2). ★ lines are `finesse` (novices skip them).
    apl: [
      { ability: B.colossusSmash, when: [{ type: 'targetHpAbove', pct: 0.1 }] },
      // Ravager's Finale right after the storm (casts the override; needs the enemy target).
      { ability: B.bladestorm, target: 'enemy', when: [{ type: 'selfAura', auraId: AURA.ravagersFinaleReady }] },
      // ★ Never sit on 2 Overpower charges (Smash and Tactician add charges; the storm channels 6 s).
      { ability: B.overpower, finesse: true, when: [{ type: 'charges', ability: B.overpower, atLeast: 2 }] },
      { ability: B.bladestorm, when: [{ type: 'targets', atLeast: 3 }] },
      { ability: B.bladestorm, when: [{ type: 'selfAura', auraId: AURA.colossusSmash }, { type: 'targetHpAbove', pct: 0.25 }] },
      {
        ability: B.rend,
        when: [{ type: 'not', cond: { type: 'targets', atLeast: 3 } }, { type: 'targetNeedsAura', auraId: AURA.rend, refreshBelowMs: 5400 }, { type: 'targetHpAbove', pct: 0.15 }],
      },
      { ability: B.execute, finesse: true, when: [{ type: 'selfAura', auraId: AURA.suddenDeath, minStacks: 2 }] },
      { ability: B.cleave, when: [{ type: 'targets', atLeast: 3 }, { type: 'selfLacksAura', auraId: AURA.cleaveBuff }] },
      // Shockwave talent: Cleave turned Slam into Shockwave.
      { ability: B.slam, when: [{ type: 'targets', atLeast: 2 }, { type: 'selfAura', auraId: AURA.shockwaveReady }] },
      { ability: B.overpower, when: [{ type: 'selfAura', auraId: AURA.overpowerProc }] },
      { ability: B.overpower, when: [{ type: 'charges', ability: B.overpower, atLeast: 2 }] },
      // ★ About to cap: the next charge is back within ~2 GCDs.
      { ability: B.overpower, finesse: true, when: [{ type: 'cooldownBelow', ability: B.overpower, ms: 3500, nextCharge: true }] },
      // ★ Mortal Strike is ready on a thin Wounds stack: build one with Overpower first.
      {
        ability: B.overpower,
        finesse: true,
        when: [{ type: 'cooldownBelow', ability: B.mortalStrike, ms: 300 }, { type: 'not', cond: { type: 'targetAura', auraId: AURA.wounds, minStacks: 2 } }],
      },
      { ability: B.mortalStrike },
      // ★ Sudden Death Execute also cashes Wounds: not right before Mortal Strike comes back.
      { ability: B.execute, finesse: true, when: [{ type: 'selfAura', auraId: AURA.suddenDeath }, { type: 'not', cond: { type: 'cooldownBelow', ability: B.mortalStrike, ms: 1500 } }] },
      { ability: B.overpower },
      { ability: B.execute, when: [{ type: 'selfAura', auraId: AURA.suddenDeath }] },
      { ability: B.execute, when: [{ type: 'targetHpBelow', pct: 0.2 }, { type: 'resource', resource: 'rage', atLeast: 50 }] },
      { ability: B.cleave, when: [{ type: 'targets', atLeast: 2 }, { type: 'resource', resource: 'rage', atLeast: 60 }] },
      // ★ Slam: keep 40 Rage for Mortal Strike while it is coming up; otherwise fill.
      { ability: B.slam, when: [{ type: 'not', cond: { type: 'targets', atLeast: 3 } }, { type: 'any', of: [{ type: 'resource', resource: 'rage', atLeast: 40 }, { type: 'not', cond: { type: 'cooldownBelow', ability: B.mortalStrike, ms: 2000 } }] }] },
      // Execute range: Execute is the better filler than Slam.
      { ability: B.execute, when: [{ type: 'targetHpBelow', pct: 0.2 }, { type: 'not', cond: { type: 'cooldownBelow', ability: B.mortalStrike, ms: 2000 } }] },
      // AoE filler when Cleave is unaffordable: Slam the target anyway rather than stand idle.
      { ability: B.slam, when: [{ type: 'targets', atLeast: 3 }, { type: 'resource', resource: 'rage', atLeast: 50 }] },
    ],
  },
  [CLASS.stormcaller]: {
    classId: CLASS.stormcaller,
    role: 'dps',
    ranged: true,
    interrupt: S.windShear,
    personalDefensive: S.astralShift,
    // Rotation rework (docs/design/classes/stormcaller.md §6). The bot presses base ids; the sim resolves
    // the transforms (key 1 → Tempest, talents: key 6 → Thundercrash, key 2 → Lava Beam).
    // Single target, Maelstrom goes into Earth Shock; Earthquake is the 3+ target (or charged 2-target) spend.
    apl: [
      { ability: S.stormkeeper, when: [{ type: 'inCombat' }] },
      // Talent transforms (Ascendance's Lava Beam, Thundercrash): spend while they last.
      { ability: S.lavaBurst, when: [{ type: 'selfAura', auraId: SC_TALENT_AURA.lavaBeamReady }] },
      { ability: S.arcTorrent, when: [{ type: 'selfAura', auraId: SC_TALENT_AURA.thundercrashReady }] },
      // ★ Two Lava Burst charges on a fresh target: spend one before its Flame Shock (no crit, but the
      // recharge keeps running instead of sitting at the cap).
      { ability: S.lavaBurst, finesse: true, when: [{ type: 'charges', ability: S.lavaBurst, atLeast: 2 }] },
      { ability: S.flameShock, when: [{ type: 'targetNeedsAura', auraId: AURA.flameShock, refreshBelowMs: 5400 }, { type: 'targetHpAbove', pct: 0.1 }] },
      // Tempest about to expire (finesse): cast it before anything else.
      { ability: S.lightningBolt, finesse: true, when: [{ type: 'selfAuraRemainingBelow', auraId: AURA.tempest, ms: 5000 }] },
      // AoE: spend on Earthquake.
      { ability: S.earthquake, target: 'groundTarget', when: [{ type: 'targets', atLeast: 3 }, { type: 'resource', resource: 'maelstrom', atLeast: 60 }] },
      {
        ability: S.earthquake,
        target: 'groundTarget',
        when: [{ type: 'targets', atLeast: 2 }, { type: 'resource', resource: 'maelstrom', atLeast: 60 }, { type: 'selfAura', auraId: AURA.staticCharge, minStacks: 3 }],
      },
      // Arc Torrent on cooldown (a 30 s channel; drifting it costs more than delaying a Surge).
      { ability: S.arcTorrent, when: [{ type: 'targetHpAbove', pct: 0.05 }] },
      // Lava Burst charges next: a Lava Surge (instant, and its extra charge is wasted at the cap),
      // never sit on 2 charges, and (finesse) spend one before the next comes back. In AoE too.
      { ability: S.lavaBurst, when: [{ type: 'selfAura', auraId: AURA.lavaSurge }] },
      { ability: S.lavaBurst, when: [{ type: 'charges', ability: S.lavaBurst, atLeast: 2 }, { type: 'targetAura', auraId: AURA.flameShock }] },
      { ability: S.lavaBurst, finesse: true, when: [{ type: 'cooldownBelow', ability: S.lavaBurst, ms: 4500, nextCharge: true }, { type: 'targetAura', auraId: AURA.flameShock }] },
      // Tempest (key 1 while the override is active).
      { ability: S.lightningBolt, when: [{ type: 'selfAura', auraId: AURA.tempest }] },
      // Single-target spend: Maelstrom near the cap or full Static Charge.
      {
        ability: S.earthShock,
        when: [
          { type: 'not', cond: { type: 'targets', atLeast: 3 } },
          { type: 'resource', resource: 'maelstrom', atLeast: 50 },
          { type: 'any', of: [{ type: 'resource', resource: 'maelstrom', atLeast: 80 }, { type: 'selfAura', auraId: AURA.staticCharge, minStacks: 5 }] },
        ],
      },
      { ability: S.earthShock, finesse: true, when: [{ type: 'not', cond: { type: 'targets', atLeast: 3 } }, { type: 'selfAura', auraId: AURA.masterOfElements }, { type: 'resource', resource: 'maelstrom', atLeast: 50 }] },
      { ability: S.lavaBurst, when: [{ type: 'not', cond: { type: 'targets', atLeast: 3 } }, { type: 'targetAura', auraId: AURA.flameShock }] },
      { ability: S.chainLightning, when: [{ type: 'targets', atLeast: 2 }] },
      { ability: S.lightningBolt },
      // On the move (cast-time spells are skipped): spend Maelstrom, refresh Flame Shock early.
      { ability: S.earthShock, when: [{ type: 'moving' }, { type: 'resource', resource: 'maelstrom', atLeast: 50 }] },
      { ability: S.flameShock, when: [{ type: 'moving' }, { type: 'targetNeedsAura', auraId: AURA.flameShock, refreshBelowMs: 9000 }] },
    ],
  },
  // ---- +classes / +basekits: the five base classes (docs/design/classes/advancement.md) -----------
  // Seven-button kits that unlock one per level; the APLs press each button the moment it unlocks
  // (validChoices skips locked ones). Openers come first (Headlong, Dirty Opener, Bloodprize after a
  // kill), then the DoT, AoE on 3+, spend, build.
  [BASE_CLASS.warrior]: {
    classId: BASE_CLASS.warrior,
    role: 'tank',
    ranged: false,
    interrupt: WA.shieldCheck,
    majorDefensive: WA.ironBreath,
    majorDefensiveAura: BASE_AURA.ironBreath,
    apl: [
      // Bloodprize: heal when hurt, or before the 20 s window closes.
      { ability: WA.bloodprize, when: [{ type: 'selfAura', auraId: BASE_AURA.freshKill }, { type: 'any', of: [{ type: 'selfHpBelow', pct: 0.9 }, { type: 'selfAuraRemainingBelow', auraId: BASE_AURA.freshKill, ms: 8000 }] }] },
      // Engage from range.
      { ability: WA.headlong, when: [{ type: 'targetDistAbove', yd: 8 }] },
      { ability: WA.ironBreath, when: [{ type: 'inCombat' }, { type: 'selfHpBelow', pct: 0.4 }] },
      { ability: WA.wideArc, when: [{ type: 'inCombat' }, { type: 'enemiesNearSelf', atLeast: 3, radius: 8 }] },
      { ability: WA.rive, when: [{ type: 'targetNeedsAura', auraId: BASE_AURA.rive, refreshBelowMs: 3600 }, { type: 'targetHpAbove', pct: 0.3 }] },
      { ability: WA.breaker },
      { ability: WA.wideArc, when: [{ type: 'inCombat' }] },
      { ability: WA.bloodprize, when: [{ type: 'selfAura', auraId: BASE_AURA.freshKill }] },
    ],
  },
  [BASE_CLASS.acolyte]: {
    classId: BASE_CLASS.acolyte,
    role: 'healer',
    ranged: true,
    interrupt: AC.dazzle,
    externalDefensive: AC.aegis,
    externalDefensiveAura: BASE_AURA.aegis,
    groupHeal: AC.sanctumCircle,
    heals: { flash: AC.tend, manaResource: 'mana' },
    oocBuffs: [{ ability: AC.hearthward, when: [{ type: 'partyLacksAura', auraId: BASE_AURA.hearthward }] }],
    apl: [
      { ability: AC.aegis, target: 'self', when: [{ type: 'inCombat' }, { type: 'selfHpBelow', pct: 0.75 }, { type: 'selfLacksAura', auraId: BASE_AURA.aegis }] },
      { ability: AC.dazzle, when: [{ type: 'inCombat' }, { type: 'selfHpBelow', pct: 0.45 }, { type: 'enemiesNearSelf', atLeast: 1, radius: 6 }] },
      { ability: AC.sanctumCircle, target: 'groundTarget', when: [{ type: 'inCombat' }, { type: 'targets', atLeast: 3, radius: 8 }] },
      { ability: AC.dawnbrand, target: 'enemy', when: [{ type: 'inCombat' }, { type: 'targetNeedsAura', auraId: BASE_AURA.dawnbrand, refreshBelowMs: 3600 }, { type: 'targetHpAbove', pct: 0.2 }] },
      { ability: AC.lightlash, target: 'enemy', when: [{ type: 'inCombat' }] },
      // On the move Lightlash can't go out: keep the burn rolling instead.
      { ability: AC.dawnbrand, target: 'enemy', when: [{ type: 'inCombat' }, { type: 'moving' }] },
    ],
  },
  [BASE_CLASS.rogue]: {
    classId: BASE_CLASS.rogue,
    role: 'dps',
    ranged: false,
    interrupt: RO.throatjab,
    personalDefensive: RO.ashveil,
    // Sneak up on the next target (Nick then resolves to Dirty Opener).
    oocBuffs: [{ ability: RO.lurk, when: [{ type: 'selfLacksAura', auraId: BASE_AURA.lurk }, { type: 'targetDistBelow', yd: 35 }] }],
    apl: [
      { ability: RO.nick, when: [{ type: 'selfAura', auraId: BASE_AURA.lurk }] },
      { ability: RO.slip, when: [{ type: 'targetDistAbove', yd: 9 }] },
      { ability: RO.finale, when: [{ type: 'resource', resource: 'edge', atLeast: 4 }] },
      { ability: RO.bladefan, when: [{ type: 'enemiesNearSelf', atLeast: 3, radius: 7 }, { type: 'resource', resource: 'tempo', atLeast: 35 }] },
      { ability: RO.nick, when: [{ type: 'resource', resource: 'tempo', atLeast: 25 }] },
    ],
  },
  [BASE_CLASS.mage]: {
    classId: BASE_CLASS.mage,
    role: 'dps',
    ranged: true,
    personalDefensive: MA.glyphShield,
    blink: MA.foldStep,
    apl: [
      // Wandering Star through a pack (3+ around the target), or to open on a pack before it arrives.
      { ability: MA.wanderingStar, target: 'enemy', when: [{ type: 'targetDistBelow', yd: 26 }, { type: 'targets', atLeast: 3, radius: 8 }] },
      { ability: MA.wanderingStar, target: 'enemy', when: [{ type: 'not', cond: { type: 'inCombat' } }, { type: 'targetDistBelow', yd: 26 }, { type: 'packNearTarget', atLeast: 2, radius: 8 }] },
      { ability: MA.hoarbind, when: [{ type: 'inCombat' }, { type: 'enemiesNearSelf', atLeast: 1, radius: 7 }] },
      { ability: MA.glyphShield, when: [{ type: 'inCombat' }, { type: 'selfHpBelow', pct: 0.6 }] },
      { ability: MA.arcaneLance, when: [{ type: 'resource', resource: 'arcana', atLeast: 3 }] },
      { ability: MA.flareBurst, target: 'groundTarget', when: [{ type: 'targets', atLeast: 3, radius: 8 }] },
      { ability: MA.manaDart, when: [{ type: 'not', cond: { type: 'moving' } }] },
      { ability: MA.flareBurst, target: 'groundTarget', when: [{ type: 'moving' }, { type: 'inCombat' }] },
    ],
  },
  [BASE_CLASS.ranger]: {
    classId: BASE_CLASS.ranger,
    role: 'dps',
    ranged: true,
    interrupt: RA.pinShot,
    // Run fast between fights (it drops by itself when one starts).
    oocBuffs: [{ ability: RA.swiftfoot, when: [{ type: 'selfLacksAura', auraId: BASE_AURA.swiftfoot }, { type: 'any', of: [{ type: 'targetDistAbove', yd: 30 }, { type: 'moving' }] }] }],
    apl: [
      { ability: RA.backflip, when: [{ type: 'inCombat' }, { type: 'enemiesNearSelf', atLeast: 1, radius: 5 }, { type: 'selfHpBelow', pct: 0.8 }] },
      { ability: RA.snareCoil, target: 'groundTarget', when: [{ type: 'inCombat' }, { type: 'targetDistBelow', yd: 14 }, { type: 'targetDistAbove', yd: 4 }] },
      { ability: RA.arrowfall, target: 'groundTarget', when: [{ type: 'targets', atLeast: 3, radius: 7 }] },
      { ability: RA.longshot, when: [{ type: 'resource', resource: 'aim', atLeast: 45 }] },
      { ability: RA.trueDraw },
    ],
  },
};

function tagged(abilities: readonly AbilityDef[], tag: NonNullable<NonNullable<AbilityDef['ui']>['tags']>[number]): AbilityId | undefined {
  return abilities.find((a) => a.ui?.tags?.includes(tag))?.id;
}

/** Generic kit for classes without a hand-written one: tags drive awareness, binds 1–4 the APL. */
export function genericKit(def: ClassDef): ClassKit {
  const abilities = def.abilities;
  const rotation = (['1', '2', '3', '4'] as const).map((k) => def.binds[k]).filter((id): id is AbilityId => !!id);
  return {
    classId: def.id,
    role: def.role,
    ranged: def.melee === null,
    interrupt: tagged(abilities, 'interrupt'),
    dispel: tagged(abilities, 'dispel'),
    taunt: tagged(abilities, 'taunt'),
    activeMitigation: def.role === 'tank' ? tagged(abilities, 'defensive') : undefined,
    majorDefensive: def.role === 'tank' ? tagged(abilities, 'majorDefensive') : undefined,
    personalDefensive: def.role !== 'tank' ? (tagged(abilities, 'defensive') ?? tagged(abilities, 'majorDefensive')) : undefined,
    externalDefensive: tagged(abilities, 'externalDefensive'),
    groupHeal: tagged(abilities, 'groupHeal'),
    heals: def.role === 'healer' ? { flash: tagged(abilities, 'heal') } : undefined,
    apl: rotation.map((ability) => ({ ability })),
  };
}

/**
 * Kit for a class. Hand-written kits are filtered against the registry: buttons the
 * content does not define are dropped, so APLs degrade gracefully while content evolves.
 */
export function kitFor(classId: string, content: ContentRegistry): ClassKit | undefined {
  const def = content.class(classId);
  // +specs: a spec'd class (the hero's folded def, or a "warrior@ironclad" stranger) plays its spec's kit.
  const specKit = def?.specId ? SPEC_KITS[def.specId] : undefined;
  const known = specKit ? { ...specKit, classId } : KITS[classId];
  if (!known) return def ? genericKit(def) : undefined;
  const has = (id: AbilityId | undefined): AbilityId | undefined => (id && content.ability(id) ? id : undefined);
  return {
    ...known,
    role: def?.role ?? known.role, // +specs: the spec's role (a Mirage Rogue tanks)
    ranged: def ? def.melee === null : known.ranged,
    interrupt: has(known.interrupt),
    secondaryInterrupt: has(known.secondaryInterrupt),
    dispel: has(known.dispel),
    taunt: has(known.taunt),
    grip: has(known.grip),
    activeMitigation: has(known.activeMitigation),
    majorDefensive: has(known.majorDefensive),
    personalDefensive: has(known.personalDefensive),
    externalDefensive: has(known.externalDefensive),
    groupHeal: has(known.groupHeal),
    heals: known.heals && {
      ...known.heals,
      flash: has(known.heals.flash),
      shock: has(known.heals.shock),
      spender: has(known.heals.spender),
      aoeSpender: has(known.heals.aoeSpender),
      burst: has(known.heals.burst),
    },
    blink: has(known.blink),
    oocBuffs: known.oocBuffs?.filter((e) => content.ability(e.ability)),
    apl: known.apl.filter((e) => content.ability(e.ability)),
  };
}
